'use strict';

namespace Core {

    class ScSidebarNav {

        private element: JQuery;
        private viewport: JQuery;

        private menuLevel: number = 0;
        private activeMenu: ScSidebarMenu;
        private menus: ScSidebarMenu[] = [];
        private animating: boolean = false;

        public parentSidebar: ScSidebar;

        private selector = {
            viewport: '.c-sidebar-nav-viewport'
        };

        constructor (
            private $scope: ng.IScope,
            private animationUtil: any
        ) {
        }

        public init (element: ng.IAugmentedJQuery, sidebar: ScSidebar): void {
            this.element = element;
            this.parentSidebar = sidebar;

            this.viewport = element.find(this.selector.viewport);

            // open the top-level menu
            this.openMainMenu();

            this.$scope.$watch('sn.parentSidebar.isOpen', (val, old) => {
                if (old && !val) {
                    // sidebar has closed so reset the menu
                    this.openMainMenu();
                }
            });
        }

        public addMenu (menu: ScSidebarMenu): void {
            this.menus.push(menu);
        }

        public showMenu (menu: ScSidebarMenu): void {
            this.activeMenu = menu;

            this.menuLevel = this.activeMenu.getMenuLevel();

            // changing menu level triggers a CSS class swap on the viewport
            this.animating = true;
            this.animationUtil.waitForTransition(this.viewport).then((el) => {
                this.animating = false;
                _.each(this.menus, (m: ScSidebarMenu) => {
                    // close any other open menus that are not the active menu or one of it's ancestors
                    if (m.isOpen && m !== this.activeMenu && !_.includes(this.activeMenu.getAncestors(), m)) {
                        m.close();
                    }
                });
            });
        }

        public back (event): void {
            if (!(this.activeMenu && this.activeMenu.parentMenuItem)) {
                return;
            }
            this.activeMenu.parentMenuItem.parentMenu.open();
        }

        public close (event): void {
            this.parentSidebar.toggle();
        }

        private openMainMenu(): void {
            _.find(this.menus, {'isSubmenu': false}).open();
        }
    }

    class ScSidebarMenu {

        // Attributes
        menuTitle: string;

        public parentMenuItem: ScSidebarMenuItem|null = null;
        public parentNav: ScSidebarNav;

        public isSubmenu: boolean = false;
        public isOpen: boolean = false;

        private menuLevel: number|null = null; // this is set upon first "open" request

        constructor (
        ) {
        }

        public init (parents: [ScSidebarNav, ScSidebarMenuItem]): void {

            this.parentNav = parents[0];
            this.parentNav.addMenu(this);

            if (parents[1]) {
                // this is a submenu of a menu item
                this.isSubmenu = true;
                this.parentMenuItem = parents[1];
                this.parentMenuItem.assignSubmenu(this);
            }
        }

        public open (): void {
            this.isOpen = true;
            this.parentNav.showMenu(this);
        }

        public close (): void {
            this.isOpen = false;
        }

        public getMenuLevel (): number {
            return this.menuLevel || this.getAncestors().length;
        }

        public getAncestors (): ScSidebarMenu[] {
            let ancestors: ScSidebarMenu[] = [];
            if (this.parentMenuItem) {
                let menuItem: ScSidebarMenuItem|null = this.parentMenuItem,
                    menu: ScSidebarMenu|null;
                while (menuItem != null) {
                    menu = menuItem.parentMenu;
                    menuItem = menu.parentMenuItem ? menu.parentMenuItem : null;
                    ancestors.push(menu);
                }
            }
            return ancestors;
        }
    }

    class ScSidebarMenuItem {

        // Attributes
        label: string;
        url: string;
        cssModifier: string;
        iconName: string;
        trackingPath: string;
        hasSubmenu: boolean;

        public parentMenu: ScSidebarMenu;
        public submenu: ScSidebarMenu;

        private modifierClasses: string[] = [];
        private iconClass: string|undefined;
        private hasIcon: boolean = false;

        private className = {
            link: 'c-sidebar-nav-list-link',
            hasIcon: 'has-icon'
        };

        constructor (
            private analytics: any
        ) {
        }

        public init (menu: ScSidebarMenu): void {
            this.parentMenu = menu;

            this.cssModifier && this.modifierClasses.push(`${this.className.link}--${this.cssModifier}`);

            if (this.iconName) {
                this.modifierClasses.push(`${this.className.link}--${this.className.hasIcon}`);
                this.iconClass = `icon-${this.iconName}`;
                this.hasIcon = true;
            }

        }

        public assignSubmenu (submenu: ScSidebarMenu) {
            this.submenu = submenu;
        }

        public linkClick (event): void {
            if (this.submenu) {
                this.submenu.open();
            } else {
                // navigation link clicked
                this.trackNavigation();
                this.parentMenu.parentNav.parentSidebar.shut();
            }
        }

        private trackNavigation (): void {
            // Mixpanel link tracking
            let data: any = {};
            _.each(this.trackingPath.split('|'), (item, index) => {
                data[`Depth ${index + 1}`] = item;
            });
            this.analytics.track(
                'mixpanel',
                'MobileNavMenuClick',
                data
            );
        }
    }

    angular
        .module('core')
        .directive('scSidebarNav', (): ng.IDirective => {
            return {
                scope: {},
                restrict: 'E',
                require: '?^scSidebar',
                replace: true,
                transclude: true,
                controller: ScSidebarNav,
                controllerAs: 'sn',
                bindToController: {
                },
                template:
                    `<nav id="main-navigation">
                        <div class="sc-c-app-header sc-c-app-header--primary">
                            <div class="sc-o-flex-grid sc-o-flex-grid--items-center sc-o-flex-grid--spacing-xxxs">
                                <div
                                    class="sc-o-flex-grid-item sc-u-drag-start-xxxs"
                                    ng-if="sn.menuLevel > 0"
                                >
                                    <button
                                        class="sc-c-icon-button sc-c-icon-button--white sc-c-icon-button--xs"
                                        type="button"
                                        ng-click="sn.back($event)"
                                        aria-label="Go back to {{sn.activeMenu.parentMenuItem.parentMenu.menuTitle}}"
                                    >
                                        <sc-svg-inline
                                            name="chevron-left"
                                            class="sc-o-svg-icon sc-o-svg-icon--bi-directional sc-o-svg-icon--xs"
                                        />
                                    </button>
                                </div>
                                <div class="sc-o-flex-grid-item sc-o-flex-grid-item--fill-space">
                                    <h3 class="sc-o-heading sc-u-color-white sc-o-heading--s">
                                        {{sn.activeMenu.menuTitle}}
                                    </h3>
                                </div>
                                <div class="sc-o-flex-grid-item sc-u-drag-end-xxxs">
                                    <button
                                        class="sc-c-icon-button sc-c-icon-button--white sc-c-icon-button--xs"
                                        type="button"
                                        ng-click="sn.close($event)"
                                        aria-label="Close main menu"
                                     >
                                        <sc-svg-inline
                                            name="close"
                                            class="sc-o-svg-icon sc-o-svg-icon--xs"
                                        />
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div class="c-sidebar-nav-viewport level-{{sn.menuLevel}}"
                             ng-class="{'animating': sn.animating}"
                             ng-transclude>
                        </div>
                    </nav>`,
                link: (
                    scope : ng.IScope,
                    el: ng.IAugmentedJQuery,
                    attr: ng.IAttributes,
                    controller: ScSidebar
                ) => {
                    scope.sn.init(el, controller);
                }
            };
        })
        .directive('scSidebarMenu', (): ng.IDirective => {
            return {
                scope: {},
                restrict: 'E',
                require: ['^scSidebarNav', '?^scSidebarMenuItem'],
                replace: true,
                transclude: true,
                controller: ScSidebarMenu,
                controllerAs: 'sm',
                bindToController: {
                    menuTitle: '@'
                },
                template:   `<ul class="c-sidebar-nav-list"
                                    aria-expanded="{{sm.isOpen}}"
                                    aria-label="{{sm.menuTitle}}"
                                    role="menu"
                                    ng-transclude>
                             </ul>`,
                link: (
                    scope: ng.IScope,
                    el: ng.IAugmentedJQuery,
                    attr: ng.IAttributes,
                    controllers: [ScSidebarNav, ScSidebarMenuItem]
                ) => {
                    scope.sm.init(controllers);
                }
            };
        })
        .directive('scSidebarMenuItem', (): ng.IDirective => {
            return {
                scope: {},
                restrict: 'E',
                replace: true,
                transclude: true,
                require: '^scSidebarMenu',
                controller: ScSidebarMenuItem,
                controllerAs: 'smi',
                bindToController: {
                    label: '@',
                    url: '@',
                    iconName: '@',
                    cssModifier: '@',
                    trackingPath: '@',
                    hasSubmenu: '='
                },
                template:   `<li class="c-sidebar-nav-list__item"
                                role="none">
                                <a class="c-sidebar-nav-list-link"
                                   ng-class="smi.modifierClasses.join(' ')"
                                   role="menuitem"
                                   tabindex="-1"
                                   ng-attr-aria-haspopup="{{smi.hasSubmenu || undefined}}"
                                   ng-attr-aria-expanded="{{smi.hasSubmenu ? smi.submenu.isOpen : undefined}}"
                                   ng-attr-href="{{smi.hasSubmenu ? '#' : smi.url}}"
                                   ng-click="smi.linkClick($event)">
                                   <i ng-if="smi.hasIcon"
                                      class="c-sidebar-nav-list-link__icon"
                                      ng-class="smi.iconClass">
                                   </i>
                                   <i ng-if="smi.hasSubmenu"
                                      class="c-sidebar-nav-list-link__submenu-icon icon-chevron-forward">
                                   </i>
                                    <span>{{smi.label}}</span>
                                </a>
                                <div ng-if="smi.hasSubmenu"
                                     class="c-sidebar-submenu"
                                     ng-class="{'open': smi.submenu.isOpen}"
                                     ng-transclude>
                                </div>
                            </li>`,
                link: (
                    scope : ng.IScope,
                    el: ng.IAugmentedJQuery,
                    attr: ng.IAttributes,
                    menuCtrl: ScSidebarMenu
                ) => {
                    scope.isLinkItem = () => {
                      if (!scope.smi.hasSubmenu) {
                        let linkEl = el.children();
                        let labelEl = linkEl.find('span');

                        linkEl.attr('itemprop', 'url');
                        labelEl.attr('itemprop', 'name');
                      }
                    };

                    scope.smi.init(menuCtrl);
                    scope.isLinkItem();
                }
            };
        });
}
